import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const FULL = { x: 0, y: 0, w: 1, h: 1 };
// Smallest crop, as a share of the shown image's side
const MIN_SHARE = 0.04;
// Auto-trim: how far (0-255, per channel) a pixel may differ from the margin colour
const TRIM_TOLERANCE = 18;
const CORNERS = ['nw', 'ne', 'se', 'sw'];
const EDGES = ['n', 'e', 's', 'w'];
// value = width / height; 'original' = the image's own ratio
const ASPECTS = [
  { key: 'free', label: 'Free', icon: 'bi-bounding-box', value: null },
  { key: 'original', label: 'Original', icon: 'bi-image', value: 'original' },
  { key: '1:1', label: '1:1', icon: 'bi-square', value: 1 },
  { key: '4:3', label: '4:3', icon: 'bi-aspect-ratio', value: 4 / 3 },
  { key: '16:9', label: '16:9', icon: 'bi-aspect-ratio', value: 16 / 9 },
  { key: '3:2', label: '3:2', icon: 'bi-aspect-ratio', value: 3 / 2 },
];

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// The biggest centred box of ratio r (width / height, in pixels) on an image of size n
const fitAspect = (r, n) => {
  if (!r || !n) return FULL;
  const k = n.w / (n.h * r); // box height share per unit of width share
  const w = k <= 1 ? 1 : 1 / k;
  const h = w * k;
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
};

/**
 * Crop popup for a figure in an agent response, laid out like a phone's photo
 * editor: aspect presets, rotate and flip, a box with corner handles over a dark
 * canvas. Everything happens in the browser; Apply hands back a PNG blob and Save
 * Preset uploads it.
 *
 * props:
 *   src          - URL to show the image: a local object URL (pasted image) or the
 *                  stored image's URL, which the storage must serve with CORS
 *   title        - heading: the figure, e.g. "Figure 3"
 *   subtitle     - optional figure subtitle (HTML / citation tokens), shown in the
 *                  same caption bar the figure has in view mode
 *   onCancel()
 *   onApply(blob, previewUrl) - previewUrl is an object URL of the cropped image
 *
 * The box is kept as shares (0..1) of the shown image, so a resize keeps the same
 * area; pixels come from the image's natural size. Rotate / flip redraw the image
 * into a local copy first, so the box always works on the picture as shown.
 */
// Subtitle as the view-mode caption bar shows it (ImageComponent): bare
// [doc_id_N]/[ar_id_N] tokens become chips and long chip labels are shortened.
const citationChipsHtml = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/(<[^>]*>)|\[doc_id_(\d+)\]/g, (m, tag, id) => tag || `<span class="ev-message-card6">doc_id_${id}</span>`)
    .replace(/(<[^>]*>)|\[ar_id_(\d+)\]/g, (m, tag, id) => tag || `<span class="ev-message-card7">ar_id_${id}</span>`)
    .replace(/<span([^>]*(?:ev-message-card6|ev-message-card7)[^>]*)>([\s\S]*?)<\/span>/g, (full, attrs, inner) => {
      const label = (inner || '').trim();
      if (label.includes('<')) return full;
      const short = label.length <= 15 ? label : `${label.slice(0, 14)}…`;
      const withTitle = /\stitle=/.test(attrs) ? attrs : `${attrs} title="${label.replace(/"/g, '&quot;')}"`;
      return `<span${withTitle}>${short}</span>`;
    })
    .replace(/className=/g, 'class=');
};

const ImageCropModal = ({ src, title = 'Crop image', subtitle = '', onCancel, onApply }) => {
  const isLocal = /^(blob|data):/.test(src || '');
  const subtitleHtml = useMemo(() => citationChipsHtml(subtitle), [subtitle]);
  // Full subtitle as plain text, for the tooltip of the one-line (trimmed) subtitle
  const subtitleText = useMemo(() => {
    if (!subtitleHtml) return '';
    const doc = new DOMParser().parseFromString(`<div>${subtitleHtml}</div>`, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  }, [subtitleHtml]);
  // A stored image is downloaded once as a blob and cropped from that local copy.
  // The signed URL is left as it is: with SigV4 every query param is signed, so
  // nothing may be added to it. no-store skips the page's cached copy, which came
  // without CORS headers (a plain <img> load) and would be refused.
  const [sourceUrl, setSourceUrl] = useState(isLocal ? src : '');

  const imgRef = useRef(null);
  const sourceImgRef = useRef(null); // the untransformed image, for rotate / flip
  const dragRef = useRef(null);
  const transformedUrlRef = useRef(null);
  const [shownUrl, setShownUrl] = useState(sourceUrl);
  const [box, setBox] = useState(FULL);
  const [natural, setNatural] = useState(null);
  const [aspectKey, setAspectKey] = useState('free');
  const [rotation, setRotation] = useState(0); // 0 / 90 / 180 / 270
  const [flipped, setFlipped] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isLocal) {
      setSourceUrl(src);
      return undefined;
    }
    let blobUrl = '';
    let cancelled = false;
    fetch(src, { mode: 'cors', cache: 'no-store' })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(b => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(b);
        setSourceUrl(blobUrl);
      })
      .catch(() => {
        if (!cancelled) setError('The image could not be opened for cropping (the storage bucket may not allow CORS for GET).');
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src, isLocal]);

  const ratio = (() => {
    const a = ASPECTS.find(x => x.key === aspectKey);
    if (!a || !a.value || !natural) return null;
    return a.value === 'original' ? natural.w / natural.h : a.value;
  })();

  const close = useCallback(() => { if (!busy) onCancel(); }, [busy, onCancel]);

  useEffect(() => () => { if (transformedUrlRef.current) URL.revokeObjectURL(transformedUrlRef.current); }, []);

  // Redraw the source rotated / flipped into a local image the box then works on
  useEffect(() => {
    const srcImg = sourceImgRef.current;
    if (!srcImg || (!rotation && !flipped)) {
      setShownUrl(sourceUrl);
      return;
    }
    const quarter = rotation % 180 !== 0;
    const canvas = document.createElement('canvas');
    canvas.width = quarter ? srcImg.naturalHeight : srcImg.naturalWidth;
    canvas.height = quarter ? srcImg.naturalWidth : srcImg.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    if (flipped) ctx.scale(-1, 1);
    ctx.drawImage(srcImg, -srcImg.naturalWidth / 2, -srcImg.naturalHeight / 2);
    try {
      canvas.toBlob((b) => {
        if (!b) return;
        if (transformedUrlRef.current) URL.revokeObjectURL(transformedUrlRef.current);
        transformedUrlRef.current = URL.createObjectURL(b);
        setShownUrl(transformedUrlRef.current);
      }, 'image/png');
    } catch (e) {
      setError('The browser is not allowed to read this stored image (the storage bucket needs CORS for GET).');
    }
  }, [rotation, flipped, sourceUrl]);

  const startDrag = (mode) => (e) => {
    if (busy || !imgRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const r = imgRef.current.getBoundingClientRect();
    dragRef.current = { mode, sx: e.clientX, sy: e.clientY, w: r.width, h: r.height, start: box };
    setDragging(true);
  };

  useEffect(() => {
    const move = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = (e.clientX - d.sx) / d.w;
      const dy = (e.clientY - d.sy) / d.h;
      const s = d.start;
      if (d.mode === 'move') {
        setBox({ ...s, x: clamp(s.x + dx, 0, 1 - s.w), y: clamp(s.y + dy, 0, 1 - s.h) });
        return;
      }
      if (d.ratio) {
        // Locked ratio: a corner moves, the opposite corner stays, height follows width
        const k = d.ratio;
        const right = s.x + s.w, bottom = s.y + s.h;
        const grow = d.mode.includes('e') ? dx : -dx;
        const maxW = Math.min(d.mode.includes('e') ? 1 - s.x : right, (d.mode.includes('s') ? 1 - s.y : bottom) / k);
        const w = clamp(s.w + grow, MIN_SHARE, maxW);
        const h = w * k;
        setBox({
          x: d.mode.includes('e') ? s.x : right - w,
          y: d.mode.includes('s') ? s.y : bottom - h,
          w,
          h,
        });
        return;
      }
      let { x, y, w, h } = s;
      if (d.mode.includes('w')) { const nx = clamp(s.x + dx, 0, s.x + s.w - MIN_SHARE); w = s.w + (s.x - nx); x = nx; }
      if (d.mode.includes('e')) { w = clamp(s.w + dx, MIN_SHARE, 1 - s.x); }
      if (d.mode.includes('n')) { const ny = clamp(s.y + dy, 0, s.y + s.h - MIN_SHARE); h = s.h + (s.y - ny); y = ny; }
      if (d.mode.includes('s')) { h = clamp(s.h + dy, MIN_SHARE, 1 - s.y); }
      setBox({ x, y, w, h });
    };
    const up = () => { dragRef.current = null; setDragging(false); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, []);

  // A corner drag under a locked ratio carries the box's height-per-width factor
  const startCorner = (c) => (e) => {
    startDrag(c)(e);
    if (dragRef.current && ratio && natural) dragRef.current.ratio = natural.w / (natural.h * ratio);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [close]);

  const chooseAspect = (key) => {
    setAspectKey(key);
    const a = ASPECTS.find(x => x.key === key);
    if (!a?.value || !natural) return; // Free keeps the box as it is
    setBox(fitAspect(a.value === 'original' ? natural.w / natural.h : a.value, natural));
  };

  const rotate = (deg) => {
    setRotation(r => (r + deg + 360) % 360);
    setBox(FULL);
    setAspectKey('free');
  };
  const flip = () => { setFlipped(f => !f); setBox(FULL); };
  const reset = () => { setRotation(0); setFlipped(false); setBox(FULL); setAspectKey('free'); };

  const px = natural && {
    x: Math.round(box.x * natural.w),
    y: Math.round(box.y * natural.h),
    width: Math.round(box.w * natural.w),
    height: Math.round(box.h * natural.h),
  };
  const untouched = box.x === 0 && box.y === 0 && box.w === 1 && box.h === 1 && !rotation && !flipped;

  const apply = async () => {
    if (!px || untouched || !imgRef.current) return;
    setBusy(true);
    setError('');
    try {
      const canvas = document.createElement('canvas');
      canvas.width = px.width;
      canvas.height = px.height;
      canvas.getContext('2d').drawImage(imgRef.current, px.x, px.y, px.width, px.height, 0, 0, px.width, px.height);
      // toBlob throws (SecurityError) when the image came from a server without CORS
      const blob = await new Promise((resolve, reject) => {
        try {
          canvas.toBlob(b => (b ? resolve(b) : reject(new Error('The image could not be cropped'))), 'image/png');
        } catch (err) {
          reject(err);
        }
      });
      onApply(blob, URL.createObjectURL(blob));
    } catch (e) {
      setError(e?.name === 'SecurityError'
        ? 'The browser is not allowed to read this stored image (the storage bucket needs CORS for GET).'
        : (e?.message || 'The image could not be cropped'));
      setBusy(false);
    }
  };

  // Auto-trim: shrink the box to the content, dropping the plain margin around it
  // (the colour of the top-left pixel). Chart screenshots usually carry a white border.
  const autoTrim = () => {
    const img = imgRef.current;
    if (!img || !natural) return;
    setError('');
    try {
      const scale = Math.min(1, 800 / Math.max(natural.w, natural.h));
      const w = Math.max(1, Math.round(natural.w * scale));
      const h = Math.max(1, Math.round(natural.h * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      const bg = [data[0], data[1], data[2], data[3]];
      const isBg = (i) => Math.abs(data[i] - bg[0]) <= TRIM_TOLERANCE && Math.abs(data[i + 1] - bg[1]) <= TRIM_TOLERANCE
        && Math.abs(data[i + 2] - bg[2]) <= TRIM_TOLERANCE && Math.abs(data[i + 3] - bg[3]) <= TRIM_TOLERANCE;
      let top = h, left = w, bottom = -1, right = -1;
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          if (!isBg((y * w + x) * 4)) {
            if (y < top) top = y;
            if (y > bottom) bottom = y;
            if (x < left) left = x;
            if (x > right) right = x;
          }
        }
      }
      if (bottom < 0) return; // a single plain colour: nothing to trim to
      setAspectKey('free');
      setBox({ x: left / w, y: top / h, w: (right - left + 1) / w, h: (bottom - top + 1) / h });
    } catch (e) {
      setError('The browser is not allowed to read this stored image (the storage bucket needs CORS for GET).');
    }
  };

  const pct = (v) => `${v * 100}%`;

  return createPortal((
    <div className="genbi-inplace-modal-overlay" onMouseDown={close}>
      <div
        open
        className="ev-card-2 genbi-fig-modal genbi-add-figure-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="genbi-crop-title"
        onMouseDown={e => e.stopPropagation()}
      >
        <header className="genbi-fig-modal-header genbi-crop-header">
          <div className="genbi-fig-heading">
            <div className="genbi-fig-title-row genbi-crop-title-row">
              <h2 id="genbi-crop-title" className="genbi-fig-modal-title">Crop {title}</h2>
              {/* What has changed so far, shown only once it has */}
              {aspectKey !== 'free' && (
                <span className="genbi-fig-count"><i className="bi bi-aspect-ratio me-1" />{ASPECTS.find(a => a.key === aspectKey)?.label}</span>
              )}
              {rotation !== 0 && (
                <span className="genbi-fig-count"><i className="bi bi-arrow-clockwise me-1" />Rotated {rotation}°</span>
              )}
              {flipped && (
                <span className="genbi-fig-count"><i className="bi bi-symmetry-vertical me-1" />Flipped</span>
              )}
            </div>
            {/* The figure's subtitle on one line, trimmed; the full text is the tooltip */}
            {subtitleHtml && (
              <p className="genbi-crop-subtitle" title={subtitleText} dangerouslySetInnerHTML={{ __html: subtitleHtml }} />
            )}
          </div>
          <button type="button" className="genbi-fig-close" onClick={close} disabled={busy} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </header>

        <div className="genbi-fig-modal-body scrollable-element">
          {/* Untransformed source (always a local copy), read once for rotate / flip */}
          {sourceUrl && (
            <img
              ref={sourceImgRef}
              src={sourceUrl}
              alt=""
              className="d-none"
              onError={() => setError('The image could not be opened for cropping.')}
            />
          )}

          <div className="genbi-crop-toolbar">
            <div className="ev-view-toggle-shell genbi-crop-aspects" role="radiogroup" aria-label="Aspect ratio">
              {ASPECTS.map(a => (
                <button
                  key={a.key}
                  type="button"
                  role="radio"
                  aria-checked={aspectKey === a.key}
                  className={`ev-view-toggle-btn ${aspectKey === a.key ? 'active' : ''}`}
                  onClick={() => chooseAspect(a.key)}
                  disabled={busy || !natural}
                >
                  <i className={`bi ${a.icon} me-1`} />{a.label}
                </button>
              ))}
            </div>
            {px && (
              <span className="genbi-fig-count genbi-crop-size-pill me-auto" title="Selection of the image, in pixels">
                <b>{px.width} × {px.height}</b> of {natural.w} × {natural.h} px
              </span>
            )}
            {/* Same toggle shell as the aspect presets, so both rows share one height */}
            <div className="ev-view-toggle-shell" role="group" aria-label="Rotate, flip and trim">
              <button type="button" className="ev-view-toggle-btn" onClick={autoTrim} disabled={busy || !natural} title="Trim the plain margin around the figure">
                <i className="bi bi-magic me-1" />Auto-trim
              </button>
              <button type="button" className="ev-view-toggle-btn" onClick={() => rotate(-90)} disabled={busy || !natural} title="Rotate left" aria-label="Rotate left">
                <i className="bi bi-arrow-counterclockwise" />
              </button>
              <button type="button" className="ev-view-toggle-btn" onClick={() => rotate(90)} disabled={busy || !natural} title="Rotate right" aria-label="Rotate right">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button type="button" className={`ev-view-toggle-btn ${flipped ? 'active' : ''}`} onClick={flip} disabled={busy || !natural} title="Flip horizontally" aria-label="Flip horizontally" aria-pressed={flipped}>
                <i className="bi bi-symmetry-vertical" />
              </button>
            </div>
          </div>

          <div className="genbi-crop-canvas">
            <div className="genbi-crop-frame">
              {shownUrl ? (
                <img
                  ref={imgRef}
                  src={shownUrl}
                  alt=""
                  draggable={false}
                  className="genbi-crop-img"
                  onLoad={e => {
                    setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
                    setError('');
                  }}
                  onError={() => setError('The image could not be opened for cropping.')}
                />
              ) : !error && (
                <span className="spinner-border spinner-border-sm ev-reg-p-blue" role="status" aria-label="Loading image" />
              )}
              {natural && (
                <>
                  {/* Dimmed area outside the box */}
                  <span className="genbi-crop-dim" style={{ left: 0, top: 0, width: '100%', height: pct(box.y) }} />
                  <span className="genbi-crop-dim" style={{ left: 0, top: pct(box.y + box.h), width: '100%', bottom: 0 }} />
                  <span className="genbi-crop-dim" style={{ left: 0, top: pct(box.y), width: pct(box.x), height: pct(box.h) }} />
                  <span className="genbi-crop-dim" style={{ left: pct(box.x + box.w), top: pct(box.y), right: 0, height: pct(box.h) }} />

                  <div
                    className={`genbi-crop-box ${dragging ? 'is-dragging' : ''}`}
                    style={{ left: pct(box.x), top: pct(box.y), width: pct(box.w), height: pct(box.h) }}
                    onPointerDown={startDrag('move')}
                  >
                    <span className="genbi-crop-grid" aria-hidden="true">
                      <span /><span /><span /><span />
                    </span>
                    {CORNERS.map(c => (
                      <span key={c} className={`genbi-crop-corner genbi-crop-corner--${c}`} onPointerDown={startCorner(c)} />
                    ))}
                    {!ratio && EDGES.map(e2 => (
                      <span key={e2} className={`genbi-crop-edge genbi-crop-edge--${e2}`} onPointerDown={startDrag(e2)} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="ev-alert-card-warning ev-fs-11 mt-2 mb-0" role="alert">
              <i className="bi bi-exclamation-triangle me-1" />{error}
            </div>
          )}
        </div>

        <footer className="genbi-fig-modal-footer">
          <span>
            <button type="button" className="ev-btn-text-only-fill ev-fs-11 ev-bold-reg" onClick={reset} disabled={busy || untouched}>
              <i className="bi bi-arrow-counterclockwise me-1" />Reset
            </button>
            <span className="d-none d-md-inline"><i className="bi bi-arrows-move me-1" />Drag the box or its corners</span>
          </span>
          <div className="d-flex align-items-center gap-2">
            {/* The app's standard pill pair, as on the in-place image edit popup */}
            <button type="button" className="ev-btn-lightblue-fill-round ev-bold-reg ev-btn-small px-3" onClick={close} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="ev-btn-blue-fill-round ev-bold-reg ev-btn-small px-4" onClick={apply} disabled={busy || !px || untouched}>
              {busy ? 'Cropping…' : 'Apply crop'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  ), document.body);
};

export default ImageCropModal;
